import { Fragment, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoLabel } from '../../Badges/InfoLabel';
import { Button, ButtonOption } from '../../Fundamentals/Button';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import SegmentedControl from '../../Fundamentals/SegmentedControl/SegmentedControl';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import { formatTierRange, withSek } from '../../PriceSimulation/worksheetFormatters';
import { ProductGroupTypeLabel } from './PackageTierCard';
import usePlanFormat from './usePlanFormat';
import { chargePlanProductCount, chargePlanTitle, chargePlanTotal, pricedGroups } from './chargePlanUtils';
import { VossPricingScheme, VossPricingSchemeText } from '../../../Constants/VOSS/vossPricingScheme';
import { VossUseRevenueRecognition, VossUseRevenueRecognitionText, revenueRecognitionModes } from '../../../Constants/VOSS/vossUseRevenueRecognition';
import type { PricedGroup } from './chargePlanUtils';
import type { PackageChargePlan, PackagePlan, PackageTier } from './Packages.types';

interface GroupPricingProps extends PricedGroup {
    canEditRevenueRecognition: boolean;
    isSaving: boolean;
    isBusy: boolean;
    onRevenueRecognitionChange: (mode: VossUseRevenueRecognition) => void;
}

const GroupPricing = ({ group, pricing, isRemovedFromTier, canEditRevenueRecognition, isSaving, isBusy, onRevenueRecognitionChange }: GroupPricingProps) => {
    const { t } = useTranslation();
    const contentId = useId();
    const [isExpanded, setIsExpanded] = useState(false);
    const products = new Map((group.products ?? []).map((product) => [product.id, product]));
    const productPricings = pricing.productPricings ?? [];
    const revenueRecognition = pricing.useRevenueRecognition ?? VossUseRevenueRecognition.Inherit;

    return (
        <div className="border rounded-8 mb-8">
            <button
                type="button"
                className="bg-transparent border-0 w-100 cursorpointer d-flex align-items-center gap-12 px-12 py-8 text-left"
                aria-expanded={isExpanded}
                aria-controls={contentId}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <SpirisIcon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="sm" className="icon-neutral" />
                <SpirisIcon name="purchasing" size="sm" className="icon-neutral" />
                <span className="flex-1 min-w-0">
                    <span className="d-flex align-items-center flex-wrap gap-8">
                        {isRemovedFromTier ? (
                            <InfoLabel type="warning" title={<span className="font-weight-normal font-size-xs text-nowrap">{t('packages.removedFromTier')}</span>} />
                        ) : (
                            <span className="font-weight-semibold">{group.name}</span>
                        )}
                        {group.productGroupType !== undefined && <ProductGroupTypeLabel type={group.productGroupType} />}
                        {(pricing.invoiceGroup ?? '') !== '' && <InfoLabel title={<span className="font-weight-normal font-size-xs">{pricing.invoiceGroup}</span>} />}
                    </span>
                    {(pricing.articleNumber ?? '') !== '' && <code className="d-block text-secondary font-weight-normal font-size-xs">{pricing.articleNumber}</code>}
                </span>
                <span className="text-right flex-shrink-0">
                    <span className="d-block font-weight-semibold text-nowrap">{withSek(pricing.price ?? 0)}</span>
                    <span className="d-block text-secondary font-13">{t('packages.productCount', { count: productPricings.length })}</span>
                </span>
            </button>
            <div className="d-flex align-items-center flex-wrap gap-12 border-top px-12 py-8">
                <span className="text-secondary font-13">{t('packages.useRevenueRecognition')}</span>
                {canEditRevenueRecognition ? (
                    <SegmentedControl<VossUseRevenueRecognition>
                        size="sm"
                        ariaLabel={t('packages.useRevenueRecognitionFor', { name: group.name })}
                        value={revenueRecognition}
                        onChange={(mode) => {
                            if (mode !== revenueRecognition) onRevenueRecognitionChange(mode);
                        }}
                        options={revenueRecognitionModes.map((mode) => ({ value: mode, label: t(VossUseRevenueRecognitionText[mode]), disabled: isBusy }))}
                    />
                ) : (
                    <InfoLabel title={<span className="font-weight-normal font-size-xs">{t(VossUseRevenueRecognitionText[revenueRecognition])}</span>} />
                )}
                {isSaving && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
            </div>
            {isExpanded && (
                <div id={contentId} className="border-top overflow-auto">
                    <table className="table mb-0">
                        <thead>
                            <tr>
                                <th>{t('packages.product')}</th>
                                <th>{t('packages.pricingModel')}</th>
                                <th className="text-right">{t('packages.includedQuantity')}</th>
                                <th className="text-right">{t('packages.maxQuantity')}</th>
                                <th className="text-right">{t('packages.pricePerUnit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productPricings.map((productPricing) => {
                                const product = products.get(productPricing.productId);
                                const isTiered = productPricing.pricingScheme === VossPricingScheme.TierPricing;
                                const steps = isTiered ? (productPricing.tierPricing ?? []) : [];

                                return (
                                    <Fragment key={productPricing.productId}>
                                        <tr>
                                            <td>
                                                <span className="d-block">
                                                    {product === undefined ? (
                                                        <InfoLabel
                                                            type="warning"
                                                            title={<span className="font-weight-normal font-size-xs text-nowrap">{t('packages.removedFromTier')}</span>}
                                                        />
                                                    ) : (
                                                        product.name
                                                    )}
                                                </span>
                                                <code className="text-secondary font-weight-normal font-size-xs">{productPricing.articleNumber ?? product?.articleNumber}</code>
                                            </td>
                                            <td className="text-nowrap">
                                                <InfoLabel
                                                    type={isTiered ? 'info' : undefined}
                                                    title={
                                                        <span className="font-weight-normal font-size-xs">
                                                            {t(VossPricingSchemeText[productPricing.pricingScheme ?? VossPricingScheme.UnitPricing])}
                                                        </span>
                                                    }
                                                />
                                            </td>
                                            <td className="text-right">{productPricing.includedQuantity ?? 0}</td>
                                            <td className="text-right">{productPricing.maxQuantity ?? '∞'}</td>
                                            <td className="text-right text-nowrap">{isTiered ? t('packages.seeSteps') : withSek(productPricing.unitPrice ?? 0)}</td>
                                        </tr>
                                        {steps.map((step, index) => (
                                            <tr key={step.index ?? index}>
                                                <td colSpan={4} className="text-secondary font-13 pl-32">
                                                    ↳ {t('packages.stepUnits', { range: formatTierRange(steps, index) })}
                                                </td>
                                                <td className="text-right text-nowrap font-13">
                                                    {withSek(step.unitPrice ?? 0)}
                                                    {(step.flatPrice ?? 0) > 0 && (
                                                        <span className="d-block text-secondary">{t('packages.plusFlatPrice', { price: withSek(step.flatPrice ?? 0) })}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

interface PackageChargePlanDrawerProps {
    chargePlan: PackageChargePlan;
    tier: PackageTier | undefined;
    plan: PackagePlan | undefined;
    canEditRevenueRecognition: boolean;
    savingGroupId: string | null;
    onRevenueRecognitionChange: (productGroupId: string, mode: VossUseRevenueRecognition) => void;
    onClose: () => void;
    onEdit?: () => void;
}

const PackageChargePlanDrawer = ({
    chargePlan,
    tier,
    plan,
    canEditRevenueRecognition,
    savingGroupId,
    onRevenueRecognitionChange,
    onClose,
    onEdit,
}: PackageChargePlanDrawerProps) => {
    const { t } = useTranslation();
    const { formatCycleLength } = usePlanFormat();
    const groups = pricedGroups(chargePlan, tier);

    return (
        <Drawer
            title={chargePlanTitle(tier, plan)}
            subtitle={t('packages.productGroupPricing') ?? ''}
            onClose={onClose}
            canClose={savingGroupId === null}
            footer={
                <>
                    <Button onClick={onClose}>{t('common.close')}</Button>
                    {onEdit !== undefined && (
                        <Button option={ButtonOption.PRIMARY} disabled={savingGroupId !== null} onClick={onEdit}>
                            {t('common.edit')}
                        </Button>
                    )}
                </>
            }
        >
            <div className="d-flex gap-12 mb-16">
                <div className="border rounded-8 p-12 flex-1">
                    <span className="d-block text-secondary font-size-xs text-uppercase">{t('packages.totalBasePrice')}</span>
                    <span className="d-block font-weight-semibold font-20">{withSek(chargePlanTotal(chargePlan))}</span>
                    <span className="text-secondary font-13">{formatCycleLength(plan?.subscriptionBillingPeriodCycle?.length)}</span>
                </div>
                <div className="border rounded-8 p-12 flex-1">
                    <span className="d-block text-secondary font-size-xs text-uppercase">{t('packages.productGroupsTitle')}</span>
                    <span className="d-block font-weight-semibold font-20">{groups.length}</span>
                    <span className="text-secondary font-13">{t('packages.productCount', { count: chargePlanProductCount(chargePlan) })}</span>
                </div>
            </div>
            {groups.map(({ group, pricing, isRemovedFromTier }) => (
                <GroupPricing
                    key={pricing.productGroupId}
                    group={group}
                    pricing={pricing}
                    isRemovedFromTier={isRemovedFromTier}
                    // a toggle's PUT leaves a removed group out, so it can't take a revenue recognition change
                    canEditRevenueRecognition={canEditRevenueRecognition && !isRemovedFromTier}
                    isSaving={savingGroupId === pricing.productGroupId}
                    isBusy={savingGroupId !== null}
                    onRevenueRecognitionChange={(mode) => onRevenueRecognitionChange(pricing.productGroupId ?? '', mode)}
                />
            ))}
        </Drawer>
    );
};

export default PackageChargePlanDrawer;
